import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthLink as Link } from '../auth/AuthContext';
import api from '../api';
import AuthCard from '../components/AuthCard';
import Spinner from '../components/Spinner';
import AgreementAcceptanceText from '../components/AgreementAcceptanceText';

const scopeLabels = {
    openid: 'Идентификатор аккаунта',
    profile: 'Профиль: имя, имя пользователя и аватар',
    email: 'Адрес электронной почты и статус подтверждения',
    offline_access: 'Доступ без повторного входа',
};

export default function ConsentPage() {
    const [params] = useSearchParams();
    const state = params.get('state') || '';
    const [view, setView] = useState(null);
    const [error, setError] = useState(false);
    const [accepted, setAccepted] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let active = true;
        setView(null);
        setError(false);
        setAccepted({});
        if (!state) {
            setError(true);
            return;
        }
        api.get('/api/consent', { params: { state } })
            .then(({ data }) => { if (active) setView(data); })
            .catch(() => { if (active) setError(true); });
        return () => { active = false; };
    }, [state]);

    if (error) return (
        <AuthCard title="Не удалось открыть запрос">
            <p className="text-sm text-gray-600">Запрос уже завершён, недействителен или сессия истекла. Вернитесь в сервис и начните вход заново.</p>
            <Link to="/dashboard/profile" className="text-blue-600">В личный кабинет</Link>
        </AuthCard>
    );
    if (!view) return <AuthCard title="Подтверждение авторизации"><Spinner /></AuthCard>;

    const alreadyAccepted = document => document.version != null
        && view.acceptedVersions?.[document.documentId] === document.version;
    const allAccepted = view.screen.documents.every((document, i) => document.required === false || alreadyAccepted(document) || accepted[i]);
    const fields = <>
        <input type="hidden" name="client_id" value={view.clientId} />
        <input type="hidden" name="state" value={state} />
        <input type="hidden" name="consent_version" value={view.version} />
    </>;

    // Обычный POST нужен для OAuth-редиректа, включая callback нативного приложения.
    // State выдан сервером и связан с аккаунтом; redirect_uri из URL страницы не используем.
    return (
        <AuthCard title={view.screen.title}>
            <div className="text-left">
                <p className="text-gray-900 font-semibold break-words">Вход в {view.title || view.clientName || 'сервис'}</p>
                <p className="text-sm text-gray-500 break-words">Аккаунт: {view.account}</p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{view.screen.description}</p>
                <p className="text-sm font-medium text-gray-800 mt-5 mb-2">Сервис получит доступ:</p>
                <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1 mb-5">
                    <li>Идентификатор, имя пользователя, имя и аватар</li>
                    <li>Электронная почта и статус её подтверждения</li>
                    <li>Права аккаунта и дата первого входа в сервис</li>
                    {view.scopes.filter(scope => !['openid', 'profile', 'email'].includes(scope))
                        .map(scope => <li key={scope}>{scopeLabels[scope] || scope}</li>)}
                </ul>
                <form action="/oauth2/authorize" method="post" onSubmit={() => setSubmitting(true)}>
                    {fields}
                    <input type="hidden" name="decision" value="accept" />
                    {view.scopes.map(scope => <input key={scope} type="hidden" name="scope" value={scope} />)}
                    <div className="space-y-3 mb-5">
                        {view.screen.documents.map((document, i) => !alreadyAccepted(document) && (
                            <label key={i} className="flex items-start gap-3 text-sm text-gray-700">
                                <input className="mt-1 shrink-0" type="checkbox" required={document.required !== false} name={`document_${i}`} value="true"
                                       checked={!!accepted[i]} onChange={event => setAccepted({ ...accepted, [i]: event.target.checked })} />
                                <span><AgreementAcceptanceText text={document.accept_text} title={document.title} url={document.url} />
                                    {document.required === false && <span className="block text-xs text-gray-500">Необязательно</span>}
                                </span>
                            </label>
                        ))}
                    </div>
                    <button type="submit" disabled={!allAccepted || submitting}
                            className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed">
                        {submitting ? 'Продолжаем…' : 'Продолжить'}
                    </button>
                </form>
                <form action="/oauth2/authorize" method="post" className="mt-3" onSubmit={() => setSubmitting(true)}>
                    {fields}
                    <input type="hidden" name="decision" value="deny" />
                    <button type="submit" disabled={submitting} className="auth-secondary w-full bg-white text-gray-700 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">Отказаться</button>
                </form>
            </div>
        </AuthCard>
    );
}
